import React from "react";
import { ArrowRight, Zap, Users, Target, Globe, Lightbulb } from "lucide-react";

export const PodIntroduction = () => {
  const differentiators = [
    {
      icon: Target,
      title: "Outcome-Driven",
      description: "We don't just complete tasks—we own results",
      traditional: "Task completion focus",
      kolaborate: "Outcome ownership model",
    },
    {
      icon: Users,
      title: "Autonomous Teams",
      description: "Self-managing Pods that adapt and evolve",
      traditional: "Rigid hierarchical structures",
      kolaborate: "Dynamic Pod autonomy",
    },
    {
      icon: Globe,
      title: "African Excellence",
      description: "Celebrating and leveraging African talent",
      traditional: "Generic offshore model",
      kolaborate: "Pride-driven performance",
    },
    {
      icon: Lightbulb,
      title: "Strategic Partnership",
      description: "We become part of your success story",
      traditional: "Vendor relationship",
      kolaborate: "Strategic partnership",
    },
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-64 h-64 border border-brand-primary/30 rounded-full animate-pulse"></div>
        <div
          className="absolute bottom-20 right-10 w-48 h-48 border border-brand-tertiary/30 rounded-full animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Hero Introduction */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-brand-primary/10 to-brand-tertiary/10 text-brand-primary font-semibold mb-8">
            <Zap className="w-5 h-5 mr-2" />
            Introducing the Kolaborate Pod Revolution
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 text-brand-primary">
            African Professionals
            <br />
            Global Standards
          </h2>

          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
            Our structured team approach connects businesses with carefully
            vetted African professionals who bring international quality
            standards to every project. Through our terratalent partnership and
            comprehensive training programs, we ensure our teams are equipped to
            deliver results that meet global expectations.
          </p>

          <div className="bg-white border border-gray-200 rounded-3xl shadow-xl p-8 max-w-3xl mx-auto mb-12">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-brand-primary to-brand-tertiary rounded-2xl flex items-center justify-center mr-6">
                <span className="text-2xl">🌍</span>
              </div>
              <div className="text-left">
                <h3 className="text-2xl font-bold text-brand-grey">
                  The Pod Difference
                </h3>
                <p className="text-gray-500">
                  Where traditional BPO ends, African excellence begins
                </p>
              </div>
            </div>
            <blockquote className="text-lg text-gray-600 text-center">
              "Traditional BPO asks 'How cheap can we go?' Kolaborate Pods ask
              'How extraordinary can we become?'"
            </blockquote>
          </div>
        </div>

        {/* Traditional vs Kolaborate Comparison */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-center mb-12 text-brand-primary">
            Why Pods Change Everything
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {differentiators.map((diff, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
              >
                <div className="bg-brand-secondary/30 p-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-brand-primary to-brand-tertiary rounded-2xl flex items-center justify-center mb-4 mx-auto">
                    <diff.icon className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-center mb-3 text-brand-grey">
                    {diff.title}
                  </h4>
                  <p className="text-gray-600 text-center text-sm mb-6">
                    {diff.description}
                  </p>
                </div>

                <div className="p-6 space-y-4">
                  <div className="bg-red-50 border border-red-200 p-3 rounded-xl">
                    <p className="text-xs font-semibold text-red-600 mb-1">
                      TRADITIONAL BPO
                    </p>
                    <p className="text-sm text-red-700">{diff.traditional}</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 p-3 rounded-xl">
                    <p className="text-xs font-semibold text-green-600 mb-1">
                      KOLABORATE PODS
                    </p>
                    <p className="text-sm text-green-700">{diff.kolaborate}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-white border border-gray-200 rounded-3xl shadow-xl p-8 max-w-2xl mx-auto">
            <h3 className="text-3xl font-bold mb-4 text-brand-primary">
              Ready to Experience the Pod Revolution?
            </h3>
            <p className="text-lg text-gray-600 mb-8">
              Join forward-thinking companies who've discovered that the future
              of work isn't about finding the cheapest option— it's about
              finding the most extraordinary one.
            </p>
            <button className="group relative overflow-hidden bg-gradient-to-r from-brand-primary to-brand-tertiary text-white px-8 py-4 rounded-2xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <span className="relative z-10 flex items-center justify-center">
                Start Your Pod Journey
                <ArrowRight
                  className="ml-2 group-hover:translate-x-1 transition-transform"
                  size={20}
                />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-brand-tertiary to-brand-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
